import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to handle URL-based scrolling to page sections
 * 
 * @param {Object} sectionMap - Object mapping URL section IDs to their corresponding refs
 *                              Example: { "maternity-mates": maternityMatesRef, "volunteer": volunteerPageRef }
 * 
 * Usage:
 *   const volunteerPageRef = useRef();
 *   const maternityMatesRef = useRef();
 *   
 *   useScrollToSection({
 *     "volunteer": volunteerPageRef,
 *     "maternity-mates": maternityMatesRef
 *   });
 */
const useScrollToSection = (sectionMap) => {
  const location = useLocation();

  // Extract ID from URL
  const extractIdFromUrl = useCallback(() => {
    const url = window.location.href;
    const pattern = /\/([^/]+)\/?$/;
    const match = url.match(pattern);
    
    if (match && match[1]) {
      const extractedId = match[1].replace("#", "");
      return extractedId;
    }
    return null;
  }, []);

  // Scroll to section based on extracted ID
  const checkUrlAndScroll = useCallback(() => {
    const id = extractIdFromUrl();
    console.log(id);
    
    if (id && sectionMap[id]) {
      const ref = sectionMap[id];
      if (ref?.current) {
        // Helper function to scroll with navbar offset compensation
        const scrollToElement = () => {
          const isMobile = window.innerWidth < 768; // md breakpoint
          
          if (isMobile) {
            // Mobile: no offset needed, navbar is at bottom
            ref.current.scrollIntoView({ behavior: "smooth" });
          } else {
            // Desktop: compensate for fixed navbar height
            const elementPosition = ref.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 40; // 80px navbar offset
            
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        };

        // Initial scroll after a short delay to let the page render
        setTimeout(() => {
          scrollToElement();
        }, 100);

        // Second scroll after animations (SectionReveal fade-ups) have settled.
        // This corrects for any layout shift caused by elements animating into
        // position (opacity 0→1, translateY 24px→0) which changes page height
        // after the first scroll has already occurred.
        setTimeout(() => {
          scrollToElement();
        }, 900);
      }
    }
  }, [extractIdFromUrl, sectionMap]);

  // Scroll on initial page load
  useEffect(() => {
    checkUrlAndScroll();
  }, [checkUrlAndScroll]);

  // Scroll when URL changes
  useEffect(() => {
    checkUrlAndScroll();
  }, [location, checkUrlAndScroll]);
};

export default useScrollToSection;
